import type React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  AddOutlined,
  MenuBookOutlined,
  DescriptionOutlined,
  ChatBubbleOutlineOutlined,
  ReceiptLongOutlined,
  ChevronRight,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import type { IStreamDetails } from "../../../../types/streams";
import apiService from "../../../../service/Api";

interface QuickActionsPanelProps {
  stream: IStreamDetails;
  streamId?: number;
  onCreateThumbnail: () => void;
  onCreateYouTubeLink: () => void;
  onNavigateToMemorial: () => void;
}

const QuickActionsPanel: React.FC<QuickActionsPanelProps> = ({
  stream,
  streamId,
  onCreateThumbnail,
  onCreateYouTubeLink,
  onNavigateToMemorial,
}) => {
  const navigate = useNavigate();
  const [hasThumbnail, setHasThumbnail] = useState(false);

  useEffect(() => {
    if (!streamId) return;
    apiService
      .get<{ results: Array<{ stream: { id: number | string } }> }>(
        "/api/configurations/deceased-thumbnails/"
      )
      .then((res) => {
        const matched = (res.data?.results ?? []).some(
          (t) => Number(t.stream.id) === Number(streamId)
        );
        setHasThumbnail(matched);
      })
      .catch(() => {});
  }, [streamId]);

  const serviceType = stream.booking?.service ?? "";

  const memorialLabel =
    serviceType === "Church" ||
    serviceType === "Other" ||
    serviceType === "Conference"
      ? "Stream Page"
      : "Memorial Page";

  const hasYouTubeLink = !!stream.youtube_stream_link;

  const actions = [
    {
      label: hasThumbnail ? "Update Thumbnail" : "Create Thumbnail",
      icon: <AddOutlined sx={{ fontSize: 18 }} />,
      onClick: onCreateThumbnail,
      iconBg: "#e8f0fe",
      iconColor: "#1a73e8",
    },
    {
      label: hasYouTubeLink ? "Update YouTube Link" : "Create YouTube Link",
      icon: <MenuBookOutlined sx={{ fontSize: 16 }} />,
      onClick: onCreateYouTubeLink,
      iconBg: "#fce8e8",
      iconColor: "#d32f2f",
    },
    {
      label: memorialLabel,
      icon: <DescriptionOutlined sx={{ fontSize: 18 }} />,
      onClick: onNavigateToMemorial,
      iconBg: "#e6f4ea",
      iconColor: "#2e7d32",
    },
    {
      label: "Open Callsheet",
      icon: <ChatBubbleOutlineOutlined sx={{ fontSize: 16 }} />,
      onClick: () => navigate(`/stream-management/view/${stream.id}/callsheet`),
      iconBg: "#fff3e0",
      iconColor: "#e65100",
    },
    {
      label: "View Booking",
      icon: <DescriptionOutlined sx={{ fontSize: 16 }} />,
      onClick: () => navigate(`/bookings/${stream.booking.id}`),
      iconBg: "#ede7f6",
      iconColor: "#5e35b1",
    },
    {
      label: "Stream Report",
      icon: <ReceiptLongOutlined sx={{ fontSize: 16 }} />,
      onClick: () => {
        const svcType = stream.booking?.service;
        const isFuneral = svcType === "Funeral" || svcType === "FuneralHome";
        const deceased = stream.booking?.deceased?.[0];
        const deceasedFullName = isFuneral
          ? [deceased?.first_name, deceased?.second_name, deceased?.surname]
              .filter(Boolean)
              .join(" ") || "N/A"
          : null;
        navigate(`/stream-management/view/${stream.id}/stream-report`, {
          state: {
            serviceType: svcType,
            deceasedName: deceasedFullName,
            streamTitle: stream.title || null,
          },
        });
      },
      iconBg: "#e0f2f1",
      iconColor: "#00695c",
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "20px",
        p: 2.5,
        boxShadow: "none",
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        sx={{
          color: "#000000",
          mb: 1.5,
        }}
      >
        Quick Actions
      </Typography>

      <List disablePadding>
        {actions.map((action) => (
          <ListItemButton
            key={action.label}
            onClick={action.onClick}
            sx={{
              py: 0.75,
              px: 0,
              borderRadius: 2,
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: `${action.iconBg}80`,
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
                width: 36,
                height: 36,
                borderRadius: "50%",
                backgroundColor: action.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: action.iconColor,
                mr: 1.5,
                flexShrink: 0,
              }}
            >
              {action.icon}
            </ListItemIcon>

            <ListItemText
              primary={action.label}
              slotProps={{
                primary: {
                  variant: "body2",
                  fontWeight: 600,
                  color: "text.primary",
                },
              }}
            />

            <ChevronRight sx={{ color: action.iconColor, fontSize: 20 }} />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default QuickActionsPanel;
